import apiClient from '../../core/http/axios';

/**
 * AI 학습 API 응답 타입
 */
export interface ViewerQueryResponse {
  success: boolean;
  error: string | null;
  data: {
    dialog_no: number;
    chat_no: number;
    message: string;
  };
  code: string | null;
}

/**
 * AI 학습 응답 타입 (내부 사용)
 */
export interface AILearningResponse {
  message: string;
  dialogNo: number;
  chatNo: number;
}

/**
 * AI 학습 요청 타입
 */
export interface AILearningRequest {
  query: string;
  //lecture_no: number;
  book_uuid: string; // book_uuid (필수)
  dialogNo?: number;
  page_no?: number; // 실제 페이지 번호(idx). DB 인덱스(page_no)가 아닙니다.
  mode?: 'mix' | 'rag' | 'llm';
}

/**
 * 대화 히스토리 메시지 타입
 */
export interface DialogHistoryMessage {
  chat_no: number;
  query: string;
  message: string;
  created_at: string;
}

/**
 * 대화 히스토리 응답 타입
 */
export interface DialogHistoryResponse {
  success: boolean;
  error: string | null;
  data: {
    dialog_no: number;
    messages: DialogHistoryMessage[];
  };
  code: string | null;
}

/**
 * 채팅 메시지 타입 (새 API)
 */
export interface DialogChatMessage {
  chat_no: number;
  type: 'A' | 'U'; // 'A' = AI, 'U' = User
  message: string;
  created_at: string;
}

/**
 * 다이얼로그 정보 타입
 */
export interface DialogInfo {
  dialog_no: number;
  dialog_uuid: string;
  anchor_page_no: number;
  title: string;
  created_at: string;
  updated_at: string;
}

/**
 * 다이얼로그 목록 응답 타입
 */
export interface DialogsListResponse {
  success: boolean;
  error: string | null;
  data: {
    dialogs: DialogInfo[];
    page: {
      next_cursor: string | null;
      has_more: boolean;
    };
  };
  code: string | null;
}

/**
 * 채팅 목록 응답 타입 (새 API)
 */
export interface DialogChatsResponse {
  success: boolean;
  error: string | null;
  data: {
    chats: DialogChatMessage[];
    page: {
      next_cursor: string | null;
      has_more: boolean;
    };
  };
  code: string | null;
}

/**
 * AI 학습 서비스
 * 사용자의 질문에 대한 AI 응답을 제공합니다.
 */
export const aiLearningService = {
  /**
   * 대화 히스토리를 불러옵니다.
   * @param bookUuid 책의 고유 ID
   * @param dialogNo 대화 번호
   */
  getDialogHistory: async (bookUuid: string, dialogNo: number): Promise<{ dialogNo: number; messages: DialogHistoryMessage[] }> => {
    try {
      const response = await apiClient.get<DialogHistoryResponse>(
        `/viewer/dialog/${dialogNo}`,
        {
          params: {
            book_uuid: bookUuid,
          },
          headers: {
            'accept': 'application/json',
          },
          timeout: 30000,
        }
      );

      if (response.data.success) {
        return {
          dialogNo: response.data.data.dialog_no,
          messages: response.data.data.messages,
        };
      } else {
        throw new Error(response.data.error || '대화 히스토리 조회 실패');
      }
    } catch (error) {
      // console.error('대화 히스토리 조회 실패:', error);
      throw new Error('대화 히스토리를 불러오는 중 오류가 발생했습니다.');
    }
  },

  /**
   * 책의 다이얼로그 목록을 불러옵니다.
   * @param bookUuid 책 UUID
   * @param limit 조회 개수 (기본값: 50)
   * @param cursor 다음 페이지 커서 (기본값: null)
   * @returns 다이얼로그 목록과 페이지 정보
   */
  getDialogs: async (
    bookUuid: string,
    limit: number = 50,
    cursor: string | null = null
  ): Promise<{ dialogs: DialogInfo[]; nextCursor: string | null; hasMore: boolean }> => {
    try {
      const params: Record<string, any> = {
        limit,
      };
      if (cursor) {
        params.cursor = cursor;
      }

      const response = await apiClient.get<DialogsListResponse>(
        `/viewer/books/${bookUuid}/dialogs`,
        {
          params,
          headers: {
            'accept': 'application/json',
          },
          timeout: 30000,
        }
      );

      if (response.data.success) {
        return {
          dialogs: response.data.data.dialogs,
          nextCursor: response.data.data.page.next_cursor,
          hasMore: response.data.data.page.has_more,
        };
      } else {
        throw new Error(response.data.error || '다이얼로그 목록 조회 실패');
      }
    } catch (error) {
      // console.error('다이얼로그 목록 조회 실패:', error);
      throw new Error('다이얼로그 목록을 불러오는 중 오류가 발생했습니다.');
    }
  },

  /**
   * dialog_uuid를 사용하여 채팅 내역을 불러옵니다.
   * @param dialogUuid 대화 UUID
   * @param limit 조회 개수 (기본값: 50)
   * @param cursor 다음 페이지 커서 (기본값: null)
   * @returns 채팅 목록과 페이지 정보
   */
  getDialogChats: async (
    dialogUuid: string,
    limit: number = 50,
    cursor: string | null = null
  ): Promise<{ chats: DialogChatMessage[]; nextCursor: string | null; hasMore: boolean }> => {
    try {
      const params: Record<string, any> = {
        limit,
      };
      if (cursor) {
        params.cursor = cursor;
      }

      const response = await apiClient.get<DialogChatsResponse>(
        `/viewer/dialogs/${dialogUuid}/chats`,
        {
          params,
          headers: {
            'accept': 'application/json',
          },
          timeout: 30000,
        }
      );

      if (response.data.success) {
        return {
          chats: response.data.data.chats,
          nextCursor: response.data.data.page.next_cursor,
          hasMore: response.data.data.page.has_more,
        };
      } else {
        throw new Error(response.data.error || '채팅 내역 조회 실패');
      }
    } catch (error) {
      // console.error('채팅 내역 조회 실패:', error);
      throw new Error('채팅 내역을 불러오는 중 오류가 발생했습니다.');
    }
  },

  /**
   * AI 학습 질문을 전송하고 응답을 받습니다.
   * 참고: 이 서비스는 항상 실제 API를 호출합니다 (Mock 미사용)
   * POST /api/viewer/query - body: { query, book_uuid, dialog_no, page_no, mode }
   * 규칙: dialog_no=0일 때 page_no는 1 이상이어야 함
   */
  sendMessage: async (request: AILearningRequest): Promise<AILearningResponse> => {
    const dialogNo = request.dialogNo ?? 0;
    let pageNo = request.page_no;
    if (dialogNo === 0 && (pageNo === undefined || pageNo < 1)) {
      pageNo = 1;
    }

    const body = {
      query: request.query,
      book_uuid: request.book_uuid,
      dialog_no: dialogNo,
      page_no: pageNo ?? 0,
      mode: request.mode ?? 'mix',
    };

    try {
      const response = await apiClient.post<ViewerQueryResponse>(
        `/viewer/query`,
        body,
        {
          headers: {
            'accept': 'application/json',
            'Content-Type': 'application/json',
          },
          timeout: 60000, // AI 학습 API는 응답이 오래 걸릴 수 있으므로 60초로 설정
        }
      );

      if (response.data.success) {
        return {
          message: response.data.data.message,
          dialogNo: response.data.data.dialog_no,
          chatNo: response.data.data.chat_no,
        };
      } else {
        throw new Error(response.data.error || 'API 요청 실패');
      }
    } catch (error) {
      // console.error('AI 학습 API 호출 실패:', error);
      throw new Error('AI 학습 응답을 받는 중 오류가 발생했습니다.');
    }
  },
};




